import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  creditScoreBandsError,
  creditScoreConfigSchema,
  DEFAULT_CREDIT_SCORE_CONFIG,
  ICreditScoreConfig,
  yupErrorMessageParse,
} from "shared";

import { Button } from "../../../../../common/Atoms/Button";
import { FormLabel } from "../../../../../common/Atoms/FormLabel";
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { Spinner } from "../../../Common/Spinner";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import {
  useFetchCreditScoreConfigQuery,
  useResetCreditScoreConfigMutation,
  useUpdateCreditScoreConfigMutation,
} from "../../../../services/api/creditScoreConfigApi/creditScoreConfig";

import { CreditScoreExamples } from "./CreditScoreExamples";

type NumberKey = Exclude<keyof ICreditScoreConfig, `bands`>;

const numberFields: { key: NumberKey; label: string; addOn: string; help: string }[] = [
  { key: `timingMaxPenalty`, label: `Late payment penalty (max)`, addOn: `points`, help: `Points removed when the contact's average days late reaches the threshold below. Scales linearly from 0.` },
  { key: `timingMaxDays`, label: `Late payment threshold`, addOn: `days`, help: `Average days late at which the full late payment penalty applies.` },
  { key: `overdueRatioMaxPenalty`, label: `Overdue share penalty (max)`, addOn: `points`, help: `Points removed when everything outstanding is overdue. Scales with the overdue share of the outstanding balance.` },
  { key: `overdueAgeMaxPenalty`, label: `Overdue age penalty (max)`, addOn: `points`, help: `Points removed when the oldest overdue invoice reaches the age cap below.` },
  { key: `overdueAgeCapDays`, label: `Overdue age cap`, addOn: `days`, help: `Age of the oldest overdue invoice at which the full overdue age penalty applies.` },
  { key: `recencyHalfLifeDays`, label: `Recency half-life`, addOn: `days`, help: `A payment this many days old counts half as much as one made today. Changing this recalculates contact stats.` },
  { key: `paymentHistoryMonths`, label: `Payment history window`, addOn: `months`, help: `Only payments within this many months count. Leave blank to use all history. Changing this recalculates contact stats.` },
];

/** Settings > Advanced > Credit score. Feature flagged; the score itself shows for every org using the defaults. */
export function CreditScoreConfig() {
  const currentOrg = useGetSelectedOrganisation();
  const organisationId = currentOrg?.id || ``;

  const { data, isLoading } = useFetchCreditScoreConfigQuery(organisationId, {
    skip: !organisationId,
  });
  const [updateConfig, { isLoading: isSaving }] = useUpdateCreditScoreConfigMutation();
  const [resetConfig, { isLoading: isResetting }] = useResetCreditScoreConfigMutation();

  const [form, setForm] = useState<ICreditScoreConfig>(DEFAULT_CREDIT_SCORE_CONFIG);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setForm(data.config);
      setError(null);
    }
  }, [data]);

  const isDirty = !!data && JSON.stringify(data.config) !== JSON.stringify(form);
  const bandsError = creditScoreBandsError(form.bands);
  const isValid = creditScoreConfigSchema.isValidSync(form);

  function setNumber(key: NumberKey, value: string | number | null) {
    setForm(prev => ({
      ...prev,
      [key]: value === `` || value === null ? null : Number(value),
    }));
  }

  function setBand(index: number, patch: { min?: number | null; label?: string }) {
    setForm(prev => ({
      ...prev,
      bands: prev.bands.map((band, i) => i === index ? { ...band, ...patch } : band),
    }));
  }

  function addBand() {
    setForm(prev => {
      const last = prev.bands[prev.bands.length - 1];
      const previous = prev.bands[prev.bands.length - 2];
      const min = Math.floor(((previous?.min ?? 100) + last.min) / 2);

      return {
        ...prev,
        bands: [...prev.bands.slice(0, -1), { min, label: `` }, last],
      };
    });
  }

  function removeBand(index: number) {
    setForm(prev => ({
      ...prev,
      bands: prev.bands.filter((_, i) => i !== index),
    }));
  }

  async function handleSave() {
    try {
      // The backend validates and trims again; this only surfaces problems before the request
      await creditScoreConfigSchema.validate(form, { abortEarly: false });

      const result = await updateConfig({ organisationId, config: form }).unwrap();

      setError(null);

      toast.success(result.statsRefreshQueued
        ? `Credit score settings saved. Contact scores will update over the next few minutes.`
        : `Credit score settings saved. Any history changes apply with the nightly refresh.`);
    }
    catch (err) {
      const fields = yupErrorMessageParse(err);
      const message = Array.isArray(fields) && fields.length
        ? fields.map(f => f.message).join(`. `)
        : `Could not save credit score settings`;

      setError(message);
      toast.error(message);
    }
  }

  async function handleReset() {
    try {
      const result = await resetConfig(organisationId).unwrap();

      setError(null);

      toast.success(result.statsRefreshQueued
        ? `Credit score settings reset to defaults. Contact scores will update over the next few minutes.`
        : `Credit score settings reset to defaults. Contact scores will update with the nightly refresh.`);
    }
    catch {
      toast.error(`Could not reset credit score settings`);
    }
  }

  if (isLoading || !data) {
    return <Spinner />;
  }

  return (
    <TwoColFormRow
      heading={ `Credit score` }
      description={ `Tune how the 0-100 payment behaviour score on each contact is calculated. Every contact starts at 100 and loses points for paying late and for the current overdue position.` }
    >
      <div className={ `space-y-6` }>
        <div className={ `grid grid-cols-1 md:grid-cols-2 gap-4` }>
          { numberFields.map(field => (
            <InputTextAddon
              key={ field.key }
              label={ field.label }
              name={ field.key }
              dataType={ `number` }
              value={ form[field.key] ?? `` }
              onChange={ e => setNumber(field.key, e.value) }
              addOnText={ field.addOn }
              helpIcon={ field.help }
              placeholder={ field.key === `paymentHistoryMonths` ? `All history` : undefined }
            />
          )) }
        </div>

        <div>
          <FormLabel>{ `Bands` }</FormLabel>
          <Paragraph variant={ `secondary` }>{ `A score falls in the first band whose minimum it reaches. The last band always starts at 0.` }</Paragraph>
          <div className={ `mt-2 space-y-2` }>
            { form.bands.map((band, index) => {
              const isLast = index === form.bands.length - 1;

              return (
                <div
                  key={ index }
                  className={ `flex items-end gap-2` }
                >
                  <InputTextAddon
                    className={ `w-32` }
                    label={ index === 0 ? `Minimum score` : undefined }
                    dataType={ `number` }
                    value={ isLast ? 0 : band.min ?? `` }
                    disabled={ isLast }
                    onChange={ e => setBand(index, { min: e.value === `` || e.value === null ? null : Number(e.value) }) }
                  />
                  <InputTextAddon
                    className={ `flex-1` }
                    label={ index === 0 ? `Label` : undefined }
                    value={ band.label }
                    onChange={ e => setBand(index, { label: String(e.value ?? ``) }) }
                    placeholder={ `e.g. Good` }
                  />
                  <TrashIcon
                    className={ form.bands.length > 2 && !isLast
                      ? `h-5 w-5 mb-2.5 text-paidniceRed-600 cursor-pointer`
                      : `h-5 w-5 mb-2.5 text-gray-300 cursor-not-allowed` }
                    onClick={ () => form.bands.length > 2 && !isLast && removeBand(index) }
                  />
                </div>
              );
            }) }
          </div>
          { bandsError && isDirty ? (
            <Paragraph
              variant={ `error` }
              className={ `mt-2` }
            >
              { bandsError }
            </Paragraph>
          ) : null }
          { form.bands.length < 8 ? (
            <Button
              className={ `mt-3` }
              color={ `white` }
              size={ `sm` }
              icon={ <PlusIcon className={ `h-4 w-4 mr-1` } /> }
              onClick={ addBand }
            >
              { `Add band` }
            </Button>
          ) : null }
        </div>

        { isValid ? (
          <CreditScoreExamples config={ form } />
        ) : (
          <Paragraph variant={ `secondary` }>{ `Fix the values above to preview example scores.` }</Paragraph>
        ) }

        { error ? (
          <Paragraph variant={ `error` }>{ error }</Paragraph>
        ) : null }

        <div className={ `flex items-center gap-3` }>
          <Button
            onClick={ handleSave }
            loading={ isSaving }
            disabled={ !isDirty || !isValid || isResetting }
          >
            { `Save` }
          </Button>
          <Button
            color={ `white` }
            onClick={ handleReset }
            loading={ isResetting }
            disabled={ data.isDefault || isSaving }
          >
            { `Reset to defaults` }
          </Button>
        </div>
      </div>
    </TwoColFormRow>
  );
}
